import React, { useState, useEffect } from "react";
import { CreditCard, Download, Clock, ExternalLink, Info } from "lucide-react";
import { toast } from "react-toastify";
import {
  getBillingInfo,
  addPaymentMethod,
  createPaymentMethodUpdateSession,
  removePaymentMethod,
  cancelSubscription,
  checkCancelledSubscription,
  reactivateSubscription,
} from "../../../api/apiFunction/billingService";
import ConfirmationModal from "./ConfirmationModal";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
};

// Custom hook for countdown timer
const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false,
  });

  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true,
        });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds, isExpired: false });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
};

// Card Icons
const MastercardIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="15"
    viewBox="0 0 256 199"
  >
    <path fill="#FF5F00" d="M93.298 16.903h69.15v124.251h-69.15z" />
    <path
      fill="#EB001B"
      d="M97.689 79.029c0-25.245 11.854-47.637 30.074-62.126C114.373 6.366 97.47 0 79.03 0C35.343 0 0 35.343 0 79.029s35.343 79.029 79.029 79.029c18.44 0 35.343-6.366 48.734-16.904c-18.22-14.269-30.074-36.88-30.074-62.125"
    />
    <path
      fill="#F79E1B"
      d="M255.746 79.029c0 43.685-35.343 79.029-79.029 79.029c-18.44 0-35.343-6.366-48.734-16.904c18.44-14.488 30.075-36.88 30.075-62.125s-11.855-47.637-30.075-62.126C141.373 6.366 158.277 0 176.717 0c43.686 0 79.03 35.563 79.03 79.029"
    />
  </svg>
);

const VisaIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="10"
    viewBox="0 0 256 114"
  >
    <path
      fill="#1A1F71"
      d="M97.197 1.46L63.68 81.418H41.814L25.32 17.604c-1-3.921-1.869-5.364-4.912-7.022C15.434 7.88 7.22 5.353 0 3.781L.487 1.46h35.204c4.48 0 8.514 2.983 9.54 8.148l8.715 46.273L75.462 1.46zm29.56 0l-17.103 79.958H88.966L106.071 1.46zm56.116 53.852c.086-21.099-29.174-22.27-28.983-31.697c.07-2.866 2.8-5.917 8.776-6.697c2.96-.383 11.138-.688 20.401 3.58l3.624-16.966C181.714 1.732 175.309 0 167.342 0c-20.45 0-34.835 10.862-34.95 26.428c-.134 11.514 10.275 17.931 18.103 21.766c8.063 3.916 10.767 6.433 10.73 9.933c-.058 5.365-6.44 7.74-12.373 7.828c-10.404.165-16.435-2.812-21.246-5.053l-3.755 17.528c4.84 2.218 13.76 4.145 22.999 4.243c21.74 0 35.959-10.737 36.023-27.36m54 26.106H256L239.29 1.46h-17.647c-3.98 0-7.325 2.31-8.809 5.861l-31.04 74.097h21.723l4.309-11.944h26.54zm-23.09-28.329l10.892-30.027l6.257 30.027zm-60.55 50.338h-8.406v7.823h9.402v2.352h-12.234V91.9h11.752v2.352h-8.92v6.857h8.405zm5.339-12.686h2.832v22.86h-2.832zm9.978 15.583c.064 3.833 2.48 5.41 5.344 5.41c2.03 0 3.285-.354 4.317-.804l.514 2.029c-.998.45-2.736.997-5.217.997c-4.796 0-7.661-3.187-7.661-7.889c0-4.7 2.767-8.372 7.308-8.372c5.12 0 6.44 4.445 6.44 7.31c0 .579-.033.997-.098 1.32zm8.307-2.028c.033-1.77-.74-4.572-3.928-4.572c-2.898 0-4.122 2.608-4.348 4.572zm18.154 8.758c-.74.355-2.383.903-4.475.903c-4.701 0-7.76-3.188-7.76-7.954c0-4.798 3.283-8.307 8.371-8.307c1.674 0 3.155.417 3.928.837l-.642 2.157c-.679-.354-1.741-.742-3.286-.742c-3.575 0-5.505 2.673-5.505 5.895c0 3.605 2.318 5.827 5.409 5.827c1.61 0 2.672-.386 3.477-.74zm7.948-18.772v3.735h4.056v2.156h-4.056v8.405c0 1.934.548 3.027 2.125 3.027c.772 0 1.224-.063 1.643-.194l.129 2.16c-.548.192-1.417.385-2.512.385c-1.321 0-2.384-.451-3.059-1.19c-.773-.871-1.095-2.254-1.095-4.09v-8.503h-2.416v-2.156h2.416V95.12zm8.013 8.598c0-1.836-.034-3.413-.129-4.863h2.48l.13 3.09h.094c.71-2.091 2.447-3.412 4.346-3.412c.291 0 .517.033.775.064v2.674c-.29-.066-.579-.066-.968-.066c-1.994 0-3.413 1.481-3.798 3.608a8.5 8.5 0 0 0-.097 1.32v8.308h-2.833zm25.111 2.8c0 5.764-4.026 8.276-7.76 8.276c-4.187 0-7.47-3.092-7.47-8.017c0-5.185 3.445-8.243 7.728-8.243c4.475 0 7.502 3.25 7.502 7.985m-12.333.162c0 3.413 1.932 5.99 4.701 5.99c2.706 0 4.733-2.546 4.733-6.055c0-2.64-1.32-5.958-4.669-5.958c-3.315 0-4.765 3.093-4.765 6.023m16.675-3.607c0-1.641-.034-2.93-.13-4.217h2.512l.161 2.576h.064c.773-1.45 2.576-2.898 5.152-2.898c2.156 0 5.506 1.288 5.506 6.631v9.275h-2.833v-8.984c0-2.51-.934-4.605-3.606-4.605c-1.836 0-3.286 1.321-3.799 2.898c-.13.355-.194.837-.194 1.321v9.37h-2.833z"
    />
  </svg>
);

const getCardIcon = (brand) => {
  if (!brand) return <CreditCard size={24} className="text-fg-60" />;
  const brandLower = brand.toLowerCase();
  if (brandLower.includes("visa")) return <VisaIcon />;
  if (brandLower.includes("master")) return <MastercardIcon />;
  return <CreditCard size={24} className="text-fg-60" />;
};

const BillingSettings = ({ userData }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRemoveCardModal, setShowRemoveCardModal] = useState(false);
  const [showReactivationInfo, setShowReactivationInfo] = useState(false);
  const [addingCard, setAddingCard] = useState(false);
  const [updatingCard, setUpdatingCard] = useState(false);
  const [removingCard, setRemovingCard] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(true);

  // Reactivation states
  const [canReactivate, setCanReactivate] = useState(false);
  const [outstandingBalance, setOutstandingBalance] = useState(0);
  const [requiresPayment, setRequiresPayment] = useState(false);
  const [canUsePlatform, setCanUsePlatform] = useState(true);
  const [accessEndDate, setAccessEndDate] = useState(null);
  const [balanceBreakdown, setBalanceBreakdown] = useState(null);

  // Check if reactivation should be disabled (within billing period)
  const isReactivationDisabled = () => {
    if (!currentSubscription) return false;

    // Only disable if subscription is cancelled and still within billing period
    if (
      (currentSubscription.status === "cancelled" ||
        currentSubscription.cancelAtPeriodEnd) &&
      currentSubscription.currentPeriodEnd
    ) {
      const now = new Date();
      const periodEnd = new Date(currentSubscription.currentPeriodEnd);

      // Disable if current date is before period end
      return now < periodEnd;
    }

    return false;
  };

  // Unified billing data from new API
  const [billingData, setBillingData] = useState(null);
  const [trialData, setTrialData] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [cardDetails, setCardDetails] = useState(null);

  // Add countdown for trial data
  const trialCountdown = useCountdown(
    trialData?.endDate || currentSubscription?.trialEnd
  );

  const [cancelReason, setCancelReason] = useState("");

  // Fetch unified billing data function (reusable)
  const fetchBillingData = async () => {
    try {
      setBillingLoading(true);
      const userId = userData?._id;

      if (!userId) {
        setBillingLoading(false);
        return;
      }

      // Call unified billing API
      const response = await getBillingInfo(userId);

      if (response?.status === 200 && response?.data) {
        const data = response.data;
        setBillingData(data);

        // Set trial data if exists
        if (data.trial) {
          setTrialData(data.trial);
        } else {
          setTrialData(null);
        }

        // Set subscription data if exists
        if (data.subscription) {
          setCurrentSubscription(data.subscription);
        } else {
          setCurrentSubscription(null);
        }

        // Set card details if exists
        if (data.cardDetails) {
          setCardDetails(data.cardDetails);
        } else {
          setCardDetails(null);
        }

        // Set payment history
        if (data.paymentHistory && data.paymentHistory.length > 0) {
          setBillingHistory(data.paymentHistory);
        } else {
          setBillingHistory([]);
        }

        // Check if subscription is cancelled and can be reactivated
        if (
          data.subscription &&
          (data.subscription.status === "cancelled" ||
            data.subscription.cancelAtPeriodEnd)
        ) {
          checkReactivationStatus(userId, data.subscription.subscriptionId);
        }
      } else {
        toast.error("Failed to fetch billing data");
      }
    } catch (error) {
      toast.error("Failed to load billing information");
    } finally {
      setBillingLoading(false);
    }
  };

  // Check if subscription can be reactivated
  const checkReactivationStatus = async (userId, subscriptionId) => {
    try {
      const response = await checkCancelledSubscription(userId);

      if (response?.status === 200 && response?.data) {
        const data = response.data;

        if (data.hasCancelledSubscription && data.canReactivate) {
          setCanReactivate(true);
          setOutstandingBalance(data.outstandingBalance || 0);
          setRequiresPayment(data.requiresPayment || false);
          setCanUsePlatform(
            data.canUsePlatform !== undefined ? data.canUsePlatform : true
          );
          setAccessEndDate(data.accessEndDate || null);
          setBalanceBreakdown(data.data?.balanceBreakdown || null);
        }
      }
    } catch (error) {}
  };

  // Fetch billing data on mount
  useEffect(() => {
    if (userData?._id) {
      fetchBillingData();
    }
  }, [userData?._id]);

  // Close reactivation info tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showReactivationInfo &&
        !event.target.closest(".reactivation-info-container")
      ) {
        setShowReactivationInfo(false);
      }
    };

    if (showReactivationInfo) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showReactivationInfo]);

  const confirmCancel = async () => {
    if (!currentSubscription?.subscriptionId) {
      toast.error("Subscription ID not found");
      return;
    }

    setLoading(true);
    try {
      const response = await cancelSubscription(
        currentSubscription.subscriptionId,
        cancelReason
      );

      if (response?.status === 200 && response?.data?.success) {
        toast.success(
          response.data.message || "Subscription cancelled successfully"
        );
        setShowCancelModal(false);
        setCancelReason("");
        // Refresh billing data instead of reloading page
        await fetchBillingData();
      } else {
        toast.error(response?.data?.message || "Failed to cancel subscription");
      }
    } catch (error) {
      toast.error("An error occurred while canceling subscription");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCard = async () => {
    if (!userData?._id) {
      toast.error("User ID not found");
      return;
    }

    setAddingCard(true);
    try {
      const response = await addPaymentMethod(userData._id);

      if (response?.status === 200 && response?.data?.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkoutUrl;
      } else {
        toast.error(
          response?.data?.message || "Failed to create checkout session"
        );
      }
    } catch (error) {
      toast.error("An error occurred while adding card");
    } finally {
      setAddingCard(false);
    }
  };

  const handleUpdateCard = async () => {
    if (!userData?._id) {
      toast.error("User ID not found");
      return;
    }

    setUpdatingCard(true);
    try {
      const response = await createPaymentMethodUpdateSession(userData._id);

      if (response?.status === 200 && response?.data?.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkoutUrl;
      } else {
        toast.error(
          response?.data?.message || "Failed to create checkout session"
        );
      }
    } catch (error) {
      toast.error("An error occurred while updating card");
    } finally {
      setUpdatingCard(false);
    }
  };

  const handleRemoveCard = () => {
    setShowRemoveCardModal(true);
  };

  const confirmRemoveCard = async () => {
    if (!userData?._id) {
      toast.error("User ID not found");
      return;
    }

    setRemovingCard(true);
    try {
      const response = await removePaymentMethod(userData._id);

      if (response?.status === 200 && response?.data?.success) {
        toast.success(
          response.data.message || "Payment method removed successfully"
        );
        setShowRemoveCardModal(false);
        // Refresh billing data instead of reloading page
        await fetchBillingData();
      } else {
        toast.error(
          response?.data?.message || "Failed to remove payment method"
        );
      }
    } catch (error) {
      toast.error("An error occurred while removing card");
    } finally {
      setRemovingCard(false);
    }
  };

  const handleReactivate = async () => {
    if (!currentSubscription?.subscriptionId) {
      toast.error("Subscription ID not found");
      return;
    }

    setReactivating(true);
    try {
      const response = await reactivateSubscription(
        currentSubscription.subscriptionId
      );

      if (response?.data?.requiresPayment) {
        // User has outstanding balance - redirect to Stripe
        if (response.data.checkoutUrl) {
          // Store IDs for verification
          localStorage.setItem("payment_session_id", response.data.sessionId);
          localStorage.setItem(
            "reactivation_subscription_id",
            currentSubscription.subscriptionId
          );

          // Redirect to Stripe checkout
          window.location.href = response.data.checkoutUrl;
        } else {
          toast.error("Failed to create payment session");
          setReactivating(false);
        }
      } else if (response?.data?.success) {
        // Subscription reactivated successfully
        toast.success("Subscription reactivated successfully!");

        // Refresh billing data
        await fetchBillingData();
        setReactivating(false);
      } else {
        toast.error(
          response?.data?.message || "Failed to reactivate subscription"
        );
        setReactivating(false);
      }
    } catch (error) {
      toast.error("An error occurred while reactivating subscription");
      setReactivating(false);
    }
  };

  const handleDownloadInvoice = (pdfUrl) => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    } else {
      toast.error("Invoice PDF not available");
    }
  };

  const handleViewInvoice = (invoiceUrl) => {
    if (invoiceUrl) {
      window.open(invoiceUrl, "_blank");
    } else {
      toast.error("Invoice URL not available");
    }
  };

  // Show loading skeleton
  if (billingLoading) {
    return (
      <div className="space-y-4">
        {/* Subscription/Trial Skeleton */}
        <div className="space-y-2">
          <div className="h-4 w-32 bg-bg-40 rounded animate-pulse"></div>
          <div className="p-3 bg-bg-60 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <div className="h-4 w-40 bg-bg-40 rounded animate-pulse"></div>
              <div className="h-4 w-24 bg-bg-40 rounded animate-pulse"></div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="h-3 w-20 bg-bg-40 rounded animate-pulse"></div>
                <div className="h-3 w-32 bg-bg-40 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center justify-between">
                <div className="h-3 w-20 bg-bg-40 rounded animate-pulse"></div>
                <div className="h-3 w-32 bg-bg-40 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Method Skeleton */}
        <div className="space-y-2">
          <div className="h-4 w-36 bg-bg-40 rounded animate-pulse"></div>
          <div className="flex items-center justify-between p-3 bg-bg-60 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-bg-40 rounded animate-pulse"></div>
              <div className="space-y-2">
                <div className="h-4 w-32 bg-bg-40 rounded animate-pulse"></div>
                <div className="h-3 w-24 bg-bg-40 rounded animate-pulse"></div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-7 w-24 bg-bg-40 rounded-full animate-pulse"></div>
              <div className="h-7 w-24 bg-bg-40 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Payment History Skeleton */}
        <div className="space-y-2">
          <div className="h-4 w-32 bg-bg-40 rounded animate-pulse"></div>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-bg-60 rounded-lg"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-6 h-6 bg-bg-40 rounded animate-pulse"></div>
                  <div className="space-y-2 flex-1">
                    <div className="h-4 w-48 bg-bg-40 rounded animate-pulse"></div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-20 bg-bg-40 rounded-full animate-pulse"></div>
                      <div className="h-3 w-24 bg-bg-40 rounded-full animate-pulse"></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="space-y-2">
                    <div className="h-4 w-16 bg-bg-40 rounded animate-pulse"></div>
                    <div className="h-3 w-12 bg-bg-40 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show no subscription/trial message
  if (!billingLoading && !currentSubscription && !trialData) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-bg-60 rounded-lg space-y-4 mt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-ac-02/20 rounded-md">
              <CreditCard size={20} strokeWidth={1.5} className="text-ac-02" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-fg-50">
                No Active Subscription or Trial
              </h3>
              <p className="text-xs text-fg-60">
                You don't have an active subscription or trial
              </p>
            </div>
          </div>
        </div>

        {/* Still show billing history if available */}
        {billingHistory.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-fg-50 mb-3">
              Payment History
            </h3>
            <div className="space-y-2">
              {billingHistory.map((item, index) => (
                <div
                  key={item.paymentId || item.chargeId || index}
                  className="flex items-center justify-between p-3 bg-bg-60 rounded-lg hover:bg-bg-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-fg-50 truncate">
                      {item.description || "Payment"}
                    </p>
                    <p className="text-xs text-fg-60">
                      {formatDate(item.createdAt || item.processedAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-sm font-medium text-fg-50">
                        {item.currency?.toUpperCase() || "$"}{" "}
                        {item.amount?.toFixed(2) || "0.00"}
                      </p>
                      <span
                        className={`text-xs font-medium ${
                          item.paymentStatus === "paid"
                            ? "text-green-400"
                            : item.paymentStatus === "failed"
                            ? "text-red-400"
                            : "text-yellow-400"
                        }`}
                      >
                        {item.paymentStatus || item.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4">
      {/* SECTION 1: Your Trial OR Your Subscription */}
      {billingData?.hasActiveTrial && trialData && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-fg-50">Your Trial</h3>
          <div className="p-3 md:p-4 bg-bg-60 rounded-lg space-y-2">
            {/* Header Row */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-fg-50">LeebAI Pro Trial</p>
              <span className="text-sm font-bold text-ac-02">
                {trialData.daysRemaining}{" "}
                <span className="text-xs text-fg-60 font-normal">
                  days remaining
                </span>
              </span>
            </div>

            {/* Trial Info */}
            <div className="text-xs text-fg-60 space-y-2 pt-2 pb-3">
              <div className="flex items-center justify-between">
                <span>Started</span>
                <span>{formatDate(trialData.startDate)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Ends</span>
                <span>{formatDate(trialData.endDate)}</span>
              </div>
            </div>

            {/* Trial Countdown Timer */}
            <div>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <p className="text-xs text-fg-60">Trial Ends In</p>
                {!trialCountdown.isExpired ? (
                  <div className="flex gap-1.5 flex-wrap">
                    <div className="text-center">
                      <div className="bg-bg-50 rounded-md py-1.5 px-2 md:py-1 md:px-2">
                        <p className="text-sm md:text-xs font-bold text-fg-50">
                          {trialCountdown.days}
                        </p>
                        <p className="text-[10px] md:text-[9px] text-fg-60">
                          Days
                        </p>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="bg-bg-50 rounded-md py-1.5 px-2 md:py-1 md:px-2">
                        <p className="text-sm md:text-xs font-bold text-fg-50">
                          {trialCountdown.hours}
                        </p>
                        <p className="text-[10px] md:text-[9px] text-fg-60">
                          Hours
                        </p>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="bg-bg-50 rounded-md py-1.5 px-2 md:py-1 md:px-2">
                        <p className="text-sm md:text-xs font-bold text-fg-50">
                          {trialCountdown.minutes}
                        </p>
                        <p className="text-[10px] md:text-[9px] text-fg-60">
                          Min
                        </p>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="bg-bg-50 rounded-md py-1.5 px-2 md:py-1 md:px-2">
                        <p className="text-sm md:text-xs font-bold text-fg-50">
                          {trialCountdown.seconds}
                        </p>
                        <p className="text-[10px] md:text-[9px] text-fg-60">
                          Sec
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-[11px] text-fg-50">
                    Your trial has expired
                  </p>
                )}
              </div>
            </div>

            {/* Status Row */}
            <div className="flex items-center justify-between pt-2">
              <p className="text-[11px] text-fg-60">
                Your trial is{" "}
                <span
                  className={
                    trialData.status === "expired"
                      ? "text-red-400"
                      : "text-green-400"
                  }
                >
                  {trialData.status === "progress"
                    ? "active"
                    : trialData.status}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Your Subscription Section */}
      {!billingData?.hasActiveTrial && currentSubscription && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-fg-50">Your Subscription</h3>
          <div className="p-3 bg-bg-60 rounded-lg space-y-2">
            {/* Header Row */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-fg-50 capitalize">
                  LeebAI {currentSubscription.plan?.name || "LeebAI Pro"}
                </p>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg-50 text-fg-60 capitalize">
                    {currentSubscription.cancelAtPeriodEnd
                      ? "Cancelled"
                      : currentSubscription.status}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg-50 text-fg-60 capitalize">
                    {currentSubscription.plan?.interval || "monthly"}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-fg-60 capitalize">
                  {currentSubscription.plan?.interval || "Monthly"}
                </p>
                <p className="text-sm font-medium text-fg-50">
                  {currentSubscription.currency?.toUpperCase() || "$"}{" "}
                  {currentSubscription.amount?.toFixed(2) ||
                    currentSubscription.plan?.price?.toFixed(2) ||
                    "0.00"}
                </p>
              </div>
            </div>

            {/* Subscription Details */}
            <div className="text-xs text-fg-60 space-y-2 pt-2">
              <div className="flex items-center justify-between">
                <span>Current Period</span>
                <span>
                  {formatDate(currentSubscription.currentPeriodStart)} -{" "}
                  {formatDate(currentSubscription.currentPeriodEnd)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Next Billing</span>
                <span>{formatDate(currentSubscription.nextBillingDate)}</span>
              </div>
            </div>

            {/* Trial Countdown if subscription has active trial */}
            {currentSubscription.isTrialActive &&
              currentSubscription.trialEnd && (
                <div className="bg-ac-02/10 rounded-lg p-3 mt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={16} className="text-ac-02" strokeWidth={1.5} />
                    <p className="text-xs font-medium text-ac-02">
                      {trialCountdown.isExpired
                        ? "Trial Expired"
                        : "Trial Ends In"}
                    </p>
                  </div>

                  {!trialCountdown.isExpired ? (
                    <div className="grid grid-cols-4 gap-2">
                      <div className="text-center">
                        <div className="bg-bg-50 rounded-md py-2 px-1">
                          <p className="text-lg font-bold text-fg-50">
                            {trialCountdown.days}
                          </p>
                          <p className="text-xs text-fg-60">Days</p>
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="bg-bg-50 rounded-md py-2 px-1">
                          <p className="text-lg font-bold text-fg-50">
                            {trialCountdown.hours}
                          </p>
                          <p className="text-xs text-fg-60">Hours</p>
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="bg-bg-50 rounded-md py-2 px-1">
                          <p className="text-lg font-bold text-fg-50">
                            {trialCountdown.minutes}
                          </p>
                          <p className="text-xs text-fg-60">Min</p>
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="bg-bg-50 rounded-md py-2 px-1">
                          <p className="text-lg font-bold text-fg-50">
                            {trialCountdown.seconds}
                          </p>
                          <p className="text-xs text-fg-60">Sec</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-2">
                      <p className="text-sm font-medium text-red-400">
                        Your trial has expired
                      </p>
                    </div>
                  )}
                </div>
              )}

            {/* Status Row with Cancel/Reactivate Button */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 pt-2">
              <p className="text-[10px] md:text-[10px] text-fg-60">
                Your subscription is{" "}
                <span
                  className={
                    currentSubscription.cancelAtPeriodEnd ||
                    currentSubscription.status === "cancelled"
                      ? "text-red-400"
                      : "text-green-400"
                  }
                >
                  {currentSubscription.cancelAtPeriodEnd ||
                  currentSubscription.status === "cancelled"
                    ? "cancelled"
                    : "active"}
                </span>
              </p>

              {/* Cancel or Reactivate Button */}
              {currentSubscription.status === "cancelled" ||
              currentSubscription.cancelAtPeriodEnd ? (
                // Show Reactivate button if cancelled
                canReactivate && (
                  <div className="flex items-center gap-2 reactivation-info-container flex-wrap">
                    <button
                      onClick={handleReactivate}
                      disabled={reactivating || isReactivationDisabled()}
                      className="px-3 py-2 md:py-1 bg-green-500/20 text-green-600 dark:text-green-400 hover:bg-green-500/30 rounded-full text-[10px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation whitespace-nowrap"
                    >
                      {reactivating
                        ? "Processing..."
                        : isReactivationDisabled()
                        ? "Reactivate (After Period End)"
                        : requiresPayment
                        ? `Pay $${outstandingBalance.toFixed(2)} & Reactivate`
                        : "Reactivate"}
                    </button>
                    {isReactivationDisabled() && (
                      <div className="relative reactivation-info-container">
                        <button
                          onClick={() =>
                            setShowReactivationInfo(!showReactivationInfo)
                          }
                          className="p-1 text-fg-60 hover:text-fg-50 transition-colors rounded-full hover:bg-bg-50"
                          title="When can I reactivate?"
                        >
                          <Info size={14} strokeWidth={2} />
                        </button>
                        {showReactivationInfo && (
                          <div className="absolute right-0 bottom-full mb-2 w-64 p-3 bg-bg-70 border border-bd-50 rounded-lg shadow-lg z-10">
                            <div className="text-xs text-fg-50 space-y-1">
                              <p className="font-semibold mb-1">
                                Reactivation Information
                              </p>
                              <p className="text-fg-60">
                                Your subscription is cancelled but you still
                                have access until the end of your current
                                billing period.
                              </p>
                              <p className="text-fg-60 mt-2">
                                <span className="font-medium text-fg-50">
                                  You can reactivate after:
                                </span>{" "}
                                <span className="text-ac-02">
                                  {formatDate(
                                    currentSubscription.currentPeriodEnd
                                  )}
                                </span>
                              </p>
                            </div>
                            <div className="absolute bottom-0 right-4 transform translate-y-full">
                              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-bg-70"></div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              ) : (
                // Show Cancel button if active
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="px-3 py-2 md:py-1 border border-red-300/30 text-red-400 hover:bg-red-50/10 dark:hover:bg-red-950/30 rounded-full text-[10px] font-medium transition-colors touch-manipulation w-full md:w-auto"
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SECTION 2: Payment Method */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-fg-50">Payment Method</h3>

        {/* Info Message about Removing Card */}
        {cardDetails && (
          <div className="flex items-start gap-2 md:gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <div className="flex-shrink-0 mt-0.5">
              <Info size={16} className="text-blue-400" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-blue-400 mb-0.5">
                Important Notice
              </p>
              <p className="text-xs text-fg-60 leading-relaxed break-words">
                Removing your card will cancel your subscription. You'll lose
                access to premium features at the end of your current billing
                period.
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-3 md:p-4 bg-bg-60 rounded-lg">
          {cardDetails ? (
            <>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex items-center justify-center flex-shrink-0 capitalize">
                  {getCardIcon(cardDetails.brand)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-fg-50 capitalize break-words">
                    {cardDetails.brand || "Card"} ••••{" "}
                    {cardDetails.lastFour || "****"}
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-bg-50 text-fg-60">
                      Expires {cardDetails.expMonth}/{cardDetails.expYear}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                <button
                  onClick={handleRemoveCard}
                  disabled={removingCard || updatingCard}
                  className="px-3 py-2 md:py-1.5 border border-red-300/70 text-red-400 hover:bg-red-50/10 dark:hover:bg-red-950/30 rounded-full text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation flex-1 md:flex-none min-w-[100px]"
                >
                  {removingCard ? "Removing..." : "Remove Card"}
                </button>
                <button
                  onClick={handleUpdateCard}
                  disabled={removingCard || updatingCard}
                  className="px-3 py-2 md:py-1.5 bg-ac-02 text-white dark:text-black rounded-full text-xs font-medium hover:bg-ac-01 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation flex-1 md:flex-none min-w-[100px]"
                >
                  {updatingCard ? "Updating..." : "Update Card"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex items-center justify-center flex-shrink-0">
                  <CreditCard size={24} className="text-fg-60" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-fg-50">No active card added</p>
                  <p className="text-xs text-fg-60">
                    Add a payment method to continue after trial
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleAddCard}
                  disabled={addingCard}
                  className="px-3 py-2 md:py-1.5 bg-ac-02 text-white dark:text-black rounded-full text-xs font-medium hover:bg-ac-01 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation w-full md:w-auto"
                >
                  {addingCard ? "Loading..." : "Add Card"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* SECTION 3: Payment History */}
      {billingHistory.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-fg-50">Payment History</h3>
          <div className="space-y-2">
            {billingHistory.map((payment, index) => (
              <div
                key={
                  payment.paymentId ||
                  payment.chargeId ||
                  payment.invoiceId ||
                  index
                }
                className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-3 md:p-4 bg-bg-60 rounded-lg"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    {payment.card?.brand ? (
                      getCardIcon(payment.card.brand)
                    ) : (
                      <CreditCard size={24} className="text-fg-60" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-fg-50 break-words">
                      {payment.description || payment.plan?.name || "Payment"}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-bg-50 text-fg-60">
                        {formatDate(payment.createdAt || payment.processedAt)}
                      </span>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-bg-50 text-fg-60 capitalize">
                        {payment.paymentType || "Subscription"}
                      </span>
                      {payment.card && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-bg-50 text-fg-60">
                          {payment.card.brand} ••••{" "}
                          {payment.card.last4 || payment.card.lastFour}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between md:justify-end gap-3 md:ml-4 flex-shrink-0">
                  <div className="text-left md:text-right">
                    <p className="text-sm font-medium text-fg-50">
                      {payment.currency?.toUpperCase() || "$"}
                      {payment.amount?.toFixed(2) || "0.00"}
                    </p>
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium inline-block mt-1 ${
                        payment.paymentStatus === "paid" ||
                        payment.status === "Paid" ||
                        payment.status === "Completed"
                          ? "bg-green-400/10 text-green-600 dark:text-green-400"
                          : payment.paymentStatus === "failed" ||
                            payment.status === "DECLINED" ||
                            payment.status === "FAILED"
                          ? "bg-red-400/10 text-red-600 dark:text-red-400"
                          : "bg-yellow-400/10 text-yellow-600 dark:text-yellow-400"
                      }`}
                    >
                      {payment.paymentStatus || payment.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {payment.invoiceUrl && (
                      <button
                        onClick={() => handleViewInvoice(payment.invoiceUrl)}
                        className="p-2 md:p-1.5 text-fg-60 hover:text-fg-50 transition-colors rounded-full touch-manipulation"
                        title="View invoice"
                      >
                        <ExternalLink
                          size={18}
                          strokeWidth={1.5}
                          className="md:w-4 md:h-4"
                        />
                      </button>
                    )}
                    {payment.invoicePdf && (
                      <button
                        onClick={() =>
                          handleDownloadInvoice(payment.invoicePdf)
                        }
                        className="p-2 md:p-1.5 text-fg-60 hover:text-fg-50 transition-colors rounded-full touch-manipulation"
                        title="Download invoice PDF"
                      >
                        <Download
                          size={18}
                          strokeWidth={1.5}
                          className="md:w-4 md:h-4"
                        />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setCancelReason("");
        }}
        onConfirm={confirmCancel}
        title="Cancel Subscription?"
        message="Are you sure you want to cancel your subscription? You'll lose access to premium features at the end of your billing period."
        confirmText="Cancel Plan"
        cancelText="Keep Subscription"
        loading={loading}
      >
        <div>
          <label className="block text-xs font-medium text-fg-50 mb-2">
            Reason for cancellation (optional)
          </label>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Tell us why you're canceling..."
            className="w-full px-3 py-2 bg-bg-50 rounded-md text-fg-50 placeholder-fg-70 text-sm focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all resize-none"
            rows="3"
            disabled={loading}
          />
        </div>
      </ConfirmationModal>

      {/* Remove Card Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemoveCardModal}
        onClose={() => setShowRemoveCardModal(false)}
        onConfirm={confirmRemoveCard}
        title="Remove Payment Method?"
        message="Are you sure you want to remove your payment method? You can still use our platform till trial end date."
        warningMessage={
          billingData?.hasActiveTrial
            ? "Your trial will end without charging. You can add a new payment method to continue after trial."
            : "Future billing attempts will fail. You can add a new payment method to resume billing."
        }
        confirmText="Remove Card"
        cancelText="Keep Card"
        loading={removingCard}
      />
    </div>
  );
};

export default BillingSettings;
